import { useState, useEffect } from 'react';

export default function Hero() {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [phoneScreen, setPhoneScreen] = useState<'recording' | 'document'>('recording');
  const [showFirst, setShowFirst] = useState(true);
  const [showTopFirst, setShowTopFirst] = useState(true);

  const backgrounds = [
    'https://readdy.ai/api/search-image?query=elegant%20modern%20corporate%20boardroom%20meeting%20with%20diverse%20legal%20professionals%20lawyers%20in%20business%20attire%20around%20glass%20conference%20table%20bright%20natural%20daylight%20streaming%20through%20floor%20to%20ceiling%20windows%20contemporary%20office%20interior%20warm%20professional%20atmosphere%20high%20key%20lighting%20cinematic%20composition%20sharp%20focus%20on%20business%20discussion%20clean%20minimalist%20design%20sophisticated%20legal%20environment%20natural%20colors%20professional%20photography%20style%20people%20visible&width=1920&height=1080&seq=hero-boardroom-lawyers-v3&orientation=landscape',
    'https://readdy.ai/api/search-image?query=professional%20male%20lawyer%20in%20tailored%20navy%20suit%20using%20iPhone%20smartphone%20in%20modern%20courtroom%20natural%20window%20light%20illuminating%20wooden%20paneling%20confident%20posture%20reviewing%20legal%20app%20contemporary%20justice%20setting%20bright%20warm%20tones%20shallow%20depth%20of%20field%20cinematic%20legal%20photography%20high%20key%20lighting%20professional%20atmosphere%20clean%20composition%20attorney%20with%20mobile%20phone&width=1920&height=1080&seq=hero-lawyer-iphone-v3&orientation=landscape',
    'https://readdy.ai/api/search-image?query=female%20paralegal%20and%20male%20attorney%20reviewing%20legal%20documents%20at%20modern%20desk%20with%20multiple%20organized%20file%20folders%20bright%20office%20environment%20natural%20daylight%20from%20large%20windows%20focused%20concentration%20professional%20attire%20contemporary%20law%20office%20interior%20warm%20lighting%20clean%20workspace%20high%20key%20photography%20legal%20research%20atmosphere%20sharp%20details%20professional%20composition%20people%20working&width=1920&height=1080&seq=hero-paralegal-attorney-v3&orientation=landscape',
    'https://readdy.ai/api/search-image?query=senior%20attorneys%20and%20legal%20team%20in%20elegant%20law%20firm%20conference%20room%20with%20floor%20to%20ceiling%20windows%20overlooking%20city%20skyline%20modern%20legal%20office%20interior%20rich%20wooden%20furniture%20professional%20setting%20bright%20natural%20daylight%20warm%20sophisticated%20atmosphere%20high%20key%20lighting%20contemporary%20legal%20workspace%20cinematic%20composition%20professional%20photography%20clean%20design%20lawyers%20visible%20in%20meeting&width=1920&height=1080&seq=hero-law-team-v4&orientation=landscape',
  ];

  // ---- Background slideshow ----
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % backgrounds.length);
    }, 5000);
    return () => clearInterval(timer);
  }, [backgrounds.length]);

  // ---- Phone screen toggle ----
  useEffect(() => {
    const screenTimer = setInterval(() => {
      setPhoneScreen((prev) => (prev === 'recording' ? 'document' : 'recording'));
    }, 4000);
    return () => clearInterval(screenTimer);
  }, []);

  // ---- Flip animation between top heading phrases ----
  useEffect(() => {
    const topFlipTimer = setInterval(() => {
      setShowTopFirst((prev) => !prev);
    }, 10000);
    return () => clearInterval(topFlipTimer);
  }, []);

  // ---- Flip animation between phrases ----
  useEffect(() => {
    const flipTimer = setInterval(() => {
      setShowFirst((prev) => !prev);
    }, 4000);
    return () => clearInterval(flipTimer);
  }, []);

  return (
    <section className="relative min-h-screen flex items-center justify-center overflow-hidden bg-white">
      {/* Background Slideshow */}
      <div className="absolute inset-0">
        {backgrounds.map((bg, index) => (
          <div
            key={index}
            className="absolute inset-0 transition-opacity duration-1000 bg-no-repeat"
            style={{
              opacity: currentSlide === index ? 1 : 0,
              backgroundImage: `url(${bg})`,
              backgroundSize: 'cover',
              backgroundPosition: 'center top',
            }}
          />
        ))}
        {/* Mobile-specific background that zooms out to show faces */}
        <style>{`
          @media (max-width: 768px) {
            .absolute.inset-0[style*="backgroundImage"] {
              background-size: 180% auto !important;
              background-position: center 20% !important;
            }
          }
        `}</style>
        {/* Light warm overlay */}
        <div className="absolute inset-0 bg-gradient-to-b from-black/20 via-black/10 to-black/25"></div>
      </div>

      {/* New Top Heading - Centered, 3-4 lines below navbar */}
      <div className="absolute top-24 sm:top-28 left-0 right-0 z-20 text-center">
        <h2 className="text-white font-serif text-3xl sm:text-4xl md:text-5xl font-light tracking-tight relative inline-block overflow-hidden" style={{ perspective: '1000px', height: '1.3em' }}>
          <span
            className="absolute inset-0 flex items-center justify-center transition-all duration-700 ease-in-out"
            style={{
              transform: showTopFirst ? 'rotateX(0deg) translateY(0)' : 'rotateX(-90deg) translateY(-50%)',
              opacity: showTopFirst ? 1 : 0,
              transformOrigin: 'center bottom',
              backfaceVisibility: 'hidden',
            }}
          >
            Secure Dictation on the Go
          </span>
          <span
            className="absolute inset-0 flex items-center justify-center transition-all duration-700 ease-in-out"
            style={{
              transform: showTopFirst ? 'rotateX(90deg) translateY(50%)' : 'rotateX(0deg) translateY(0)',
              opacity: showTopFirst ? 0 : 1,
              transformOrigin: 'center top',
              backfaceVisibility: 'hidden',
            }}
          >
            Fast Affordable Accurate
          </span>
          {/* Invisible text for sizing */}
          <span className="invisible">Secure Dictation on the Go</span>
        </h2>
      </div>

      {/* HushLegalAI Logo & Name - Moved down */}
      <div className="absolute top-40 sm:top-52 left-4 sm:left-8 z-20 flex items-center gap-3 sm:gap-4">
        <div className="w-[138px] h-[138px] sm:w-[187px] sm:h-[187px] flex items-center justify-center">
          <img
            src="https://static.readdy.ai/image/151e34d9a5807cd608014213251345d2/9e822f5c2b430562886795248cd483dd.png"
            alt="HushLegalAI"
            className="w-full h-full object-contain"
          />
        </div>
        <span className="text-white font-serif text-3xl sm:text-5xl font-light tracking-tight">HushLegalAI</span>
      </div>

      {/* Main Content - pushed down with extra top padding */}
      <div className="relative z-10 max-w-[1400px] mx-auto px-6 sm:px-8 pt-72 sm:pt-96 pb-20 sm:pb-32">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          {/* Left side */}
          <div className="text-left space-y-8">
            <h1 className="font-serif text-5xl md:text-6xl lg:text-7xl font-light text-white mb-8 leading-tight tracking-tight">
              AI Drafted Pleadings<br />
              and Contracts<br />
              <span className="relative inline-block h-[1.4em] overflow-hidden mt-2" style={{ perspective: '1000px', minWidth: '100%' }}>
                {/* First phrase: From Anywhere */}
                <span
                  className={`absolute inset-0 flex items-start transition-all duration-700 ease-in-out text-4xl md:text-5xl lg:text-6xl`}
                  style={{
                    transform: showFirst ? 'rotateX(0deg) translateY(0)' : 'rotateX(-90deg) translateY(-50%)',
                    opacity: showFirst ? 1 : 0,
                    transformOrigin: 'center bottom',
                    backfaceVisibility: 'hidden',
                  }}
                >
                  From Anywhere
                </span>
                {/* Second phrase: Privacy Protected */}
                <span
                  className={`absolute inset-0 flex items-start transition-all duration-700 ease-in-out text-4xl md:text-5xl lg:text-6xl`}
                  style={{
                    transform: showFirst ? 'rotateX(90deg) translateY(50%)' : 'rotateX(0deg) translateY(0)',
                    opacity: showFirst ? 0 : 1,
                    transformOrigin: 'center top',
                    backfaceVisibility: 'hidden',
                  }}
                >
                  Privacy Protected
                </span>
              </span>
            </h1>

            <p className="text-xl md:text-2xl text-white/95 mb-12 max-w-3xl font-light leading-relaxed">
              Client data is not stored, passes through and is destroyed
            </p>

            <div className="flex flex-col sm:flex-row gap-4 items-start">
              <button 
                onClick={() => {
                  const element = document.getElementById('final-cta');
                  if (element) {
                    element.scrollIntoView({ behavior: 'smooth' });
                  }
                }}
                className="bg-white text-gray-900 px-8 py-4 rounded-full font-medium hover:bg-gray-100 transition-all shadow-lg whitespace-nowrap cursor-pointer"
              >
                Claim Founders Spot
              </button>
            </div>

            <div className="mt-16 flex flex-wrap gap-8 text-white/90">
              <div className="flex items-center gap-2">
                <i className="ri-shield-check-line text-2xl"></i>
                <span className="font-light">Attorney-Client Privilege</span>
              </div>
              <div className="flex items-center gap-2">
                <i className="ri-delete-bin-line text-2xl"></i>
                <span className="font-light">Zero Data Retention</span>
              </div>
              <div className="flex items-center gap-2">
                <i className="ri-lock-line text-2xl"></i>
                <span className="font-light">End-to-End Encrypted</span>
              </div>
            </div>
          </div>

          {/* Right side – iPhone mockup */}
          <div className="relative flex justify-center lg:justify-end">
            <div className="relative w-[340px] h-[694px]">
              {/* iPhone Frame */}
              <div className="absolute inset-0 bg-[#1d1d1f] rounded-[55px] shadow-2xl border-[12px] border-[#1d1d1f]">
                {/* Notch */}
                <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[120px] h-[30px] bg-[#1d1d1f] rounded-b-3xl z-20"></div>

                {/* Screen */}
                <div className="relative w-full h-full bg-black rounded-[43px] overflow-hidden">
                  {/* ==== Recording Screen ==== */}
                  <div
                    className={`absolute inset-0 transition-opacity duration-500 ${
                      phoneScreen === 'recording' ? 'opacity-100' : 'opacity-0'
                    }`}
                  >
                    {/* Header – extra top padding to avoid notch overlap */}
                    <div className="pt-12 bg-[#1a1a1a] px-6 pb-4 flex items-center gap-3 border-b border-gray-800">
                      <div className="w-8 h-8 flex items-center justify-center">
                        <img
                          src="https://static.readdy.ai/image/151e34d9a5807cd608014213251345d2/8df0e05ed936c289fbef98f2071860df.png"
                          alt="HushLegal"
                          className="w-full h-full object-contain"
                        />
                      </div>
                      <span className="text-white font-semibold text-lg">HushLegal</span>
                    </div>

                    {/* Document type tabs */}
                    <div className="bg-[#1a1a1a] px-6 py-4 flex gap-3">
                      <button className="px-6 py-2 bg-[#06b6d4] text-white rounded-full text-sm font-medium whitespace-nowrap">
                        Motion
                      </button>
                      <button className="px-6 py-2 bg-[#2a2a2a] text-gray-400 rounded-full text-sm font-medium whitespace-nowrap">
                        Brief
                      </button>
                      <button className="px-6 py-2 bg-[#2a2a2a] text-gray-400 rounded-full text-sm font-medium whitespace-nowrap">
                        Contract
                      </button>
                    </div>

                    {/* Recording area */}
                    <div className="bg-[#1a1a1a] px-6 py-8">
                      <div className="bg-[#2a2a2a] rounded-2xl p-6">
                        {/* Waveform */}
                        <div className="flex items-center justify-center gap-1 h-20 mb-4">
                          {[...Array(20)].map((_, i) => (
                            <div
                              key={i}
                              className="w-1 bg-[#06b6d4] rounded-full animate-wave"
                              style={{
                                height: `${Math.random() * 60 + 20}%`,
                                animationDelay: `${i * 0.1}s`,
                              }}
                            />
                          ))}
                        </div>

                        {/* Recording status */}
                        <div className="flex items-center justify-between text-sm">
                          <span className="text-[#06b6d4] font-medium">Recording...</span>
                          <span className="text-gray-400">0:47</span>
                        </div>
                      </div>
                    </div>

                    {/* Live transcription */}
                    <div className="bg-[#1a1a1a] px-6 pb-6">
                      <div className="bg-[#2a2a2a] rounded-2xl p-5">
                        <div className="text-gray-400 text-xs mb-2">Live Transcription</div>
                        <div className="text-white text-sm leading-relaxed flex items-start">
                          <span>Opposition to summary judgment...</span>
                          <span className="inline-block w-0.5 h-4 bg-[#06b6d4] ml-1 animate-blink"></span>
                        </div>
                      </div>
                    </div>

                    {/* Generate button */}
                    <div className="bg-[#1a1a1a] px-6 pb-6">
                      <button className="w-full bg-[#06b6d4] text-black font-semibold py-4 rounded-2xl flex items-center justify-center gap-2 hover:bg-[#08d4f0] transition-colors cursor-pointer whitespace-nowrap">
                        <i className="ri-sparkling-2-fill text-lg"></i>
                        Generate Document
                      </button>
                    </div>

                    {/* Home indicator – positioned inside the black screen */}
                    <div className="absolute bottom-2 left-0 right-0 flex justify-center pb-2">
                      <div className="w-32 h-1 bg-gray-700 rounded-full"></div>
                    </div>
                  </div>

                  {/* ==== Document Screen ==== */}
                  <div
                    className={`absolute inset-0 transition-opacity duration-500 ${
                      phoneScreen === 'document' ? 'opacity-100' : 'opacity-0'
                    }`}
                  >
                    <div className="bg-white h-full overflow-hidden flex flex-col">
                      <div className="flex-1 overflow-y-auto pt-12 px-6 pb-24">
                        <div className="text-center">
                          <h2 className="text-xs font-bold text-gray-900 mb-1">
                            UNITED STATES DISTRICT COURT
                          </h2>
                          <p className="text-[10px] text-gray-500 mb-4">EASTERN DISTRICT OF MICHIGAN</p>

                          <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 mb-4 text-left">
                            <div className="flex justify-between text-[9px] mb-2">
                              <div>
                                <p className="font-bold text-gray-900">SMITH INDUSTRIES, LLC,</p>
                                <p className="text-gray-500 ml-4">Plaintiff,</p>
                                <p className="text-gray-900 my-1">v.</p>
                                <p className="font-bold text-gray-900">JONES CORPORATION,</p>
                                <p className="text-gray-500 ml-4">Defendant.</p>
                              </div>
                              <div className="text-right">
                                <p className="text-gray-500">Case No. 2:24-cv-01234</p>
                                <p className="text-gray-500">Hon. Sarah Williams</p>
                              </div>
                            </div>
                          </div>

                          <h3 className="text-[11px] font-bold text-gray-900 mb-3">
                            PLAINTIFF'S OPPOSITION TO<br />
                            DEFENDANT'S MOTION FOR SUMMARY JUDGMENT
                          </h3>

                          <div className="text-left space-y-3 text-[9px] text-gray-700">
                            <div>
                              <p className="font-bold mb-1">I. INTRODUCTION</p>
                              <p className="leading-relaxed">
                                Plaintiff Smith Industries, LLC respectfully submits this Opposition to Defendant's Motion for Summary Judgment. Genuine issues of material fact preclude summary judgment.
                              </p>
                            </div>

                            <div>
                              <p className="font-bold mb-1">II. STATEMENT OF FACTS</p>
                              <p className="leading-relaxed">
                                On March 15, 2023, the parties entered into a binding agreement whereby Defendant agreed to deliver...
                              </p>
                            </div>

                            <div>
                              <p className="font-bold mb-1">III. LEGAL STANDARD</p>
                              <p className="leading-relaxed">
                                Summary judgment is appropriate only where there is no genuine dispute as to any material fact. Fed. R. Civ. P. 56(a)...
                              </p>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Fixed download button */}
                      <div className="absolute bottom-14 left-0 right-0 px-6 bg-white pt-4">
                        <button className="w-full bg-[#06b6d4] text-black font-semibold py-4 rounded-2xl flex items-center justify-center gap-2 cursor-pointer whitespace-nowrap">
                          <i className="ri-download-line text-lg"></i>
                          Download .docx
                        </button>
                      </div>

                      {/* Home indicator – correctly positioned */}
                      <div className="absolute bottom-2 left-0 right-0 flex justify-center bg-white pb-2">
                        <div className="w-32 h-1 bg-gray-300 rounded-full"></div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Side volume / power buttons (visual only) */}
              <div className="absolute left-[-3px] top-[120px] w-[3px] h-[32px] bg-[#1d1d1f] rounded-l"></div>
              <div className="absolute left-[-3px] top-[170px] w-[3px] h-[62px] bg-[#1d1d1f] rounded-l"></div>
              <div className="absolute left-[-3px] top-[240px] w-[3px] h-[62px] bg-[#1d1d1f] rounded-l"></div>
              <div className="absolute right-[-3px] top-[190px] w-[3px] h-[82px] bg-[#1d1d1f] rounded-r"></div>
            </div>
          </div>
        </div>
      </div>

      {/* Scroll indicator */}
      <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 animate-bounce">
        <i className="ri-arrow-down-line text-white text-2xl"></i>
      </div>

      {/* Inline CSS for animations */}
      <style>{`
        @keyframes wave {
          0%, 100% { height: 20%; }
          50% { height: 80%; }
        }
        .animate-wave {
          animation: wave 1s ease-in-out infinite;
        }
        @keyframes blink {
          0%, 100% { opacity: 1; }
          50% { opacity: 0; }
        }
        .animate-blink {
          animation: blink 1s ease-in-out infinite;
        }
      `}</style>
    </section>
  );
}
